import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, TextField, CircularProgress, Typography, DialogTitle, DialogContent, DialogActions, Chip, Snackbar, Alert, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { contactService } from '../services/api';
import { Message, Reply, Search, TrendingUp, CheckCircle, Schedule } from '@mui/icons-material';

const ContactsPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [response, setResponse] = useState('');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('created_at-desc');
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Fetch inquiries whenever search, filter, sort, or page changes
  useEffect(() => {
    fetchInquiries();
  }, [searchQuery, filterStatus, sortBy, pageNumber]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await contactService.getAllInquiries({
        page: pageNumber,
        limit: 10,
        search: searchQuery,
        sort: sortBy,
        status: filterStatus === 'all' ? '' : filterStatus
      });
      
      let data = [];
      let paginationData = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
      };

      if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.success && response.data?.data) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      }
      
      setInquiries(data);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      setInquiries([]);
      setToast({ open: true, message: 'Error loading inquiries', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = (inquiry) => {
    setSelectedInquiry(inquiry);
    setResponse('');
    setOpenDialog(true);
  };

  const handleSendResponse = async () => {
    if (!response.trim()) {
      setToast({ open: true, message: 'Please enter a response', type: 'error' });
      return;
    }
    try {
      setSending(true);
      await contactService.respondToInquiry(selectedInquiry.id, response);
      setToast({ open: true, message: 'Response sent successfully!', type: 'success' });
      setOpenDialog(false);
      setResponse('');
      await fetchInquiries();
    } catch (error) {
      console.error('Error sending response:', error);
      setToast({ open: true, message: 'Error sending response: ' + (error.response?.data?.error || error.message), type: 'error' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'read': return 'success';
      case 'new': return 'warning';
      case 'responded': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
            Contact Inquiries
          </Typography>
          <Typography variant="body2" sx={{ color: '#667085' }}>
            Review and respond to user inquiries
          </Typography>
        </Box>

        {/* Stats Summary */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
          {/* Total Inquiries Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                <TrendingUp sx={{ color: '#2c5f2d', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Total Inquiries
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c5f2d', mt: 0.5 }}>
                  {pagination.totalItems}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  All messages
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* New Inquiries Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#e0e7ff', p: 2, borderRadius: '12px' }}>
                <Message sx={{ color: '#4f46e5', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  New Inquiries
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4f46e5', mt: 0.5 }}>
                  {inquiries.filter(i => i.status === 'new').length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Awaiting response
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Responded Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#fef3c7', p: 2, borderRadius: '12px' }}>
                <CheckCircle sx={{ color: '#d97706', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Responded
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706', mt: 0.5 }}>
                  {inquiries.filter(i => i.status === 'responded').length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Completed
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Search and Filter Section */}
        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name, email, subject..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: '#667085' }} />,
                }}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e8ecf1' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPageNumber(1);
                  }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="responded">Responded</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPageNumber(1);
                  }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="created_at-desc">Date (Newest)</MenuItem>
                  <MenuItem value="created_at-asc">Date (Oldest)</MenuItem>
                  <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                  <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ 
          border: '1px solid #e8ecf1',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb', borderBottom: '2px solid #e8ecf1' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inquiries && inquiries.length > 0 ? (
                  inquiries.map(inquiry => (
                    <TableRow 
                      key={inquiry.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8fafb' },
                        borderBottom: '1px solid #e8ecf1'
                      }}
                    >
                      <TableCell sx={{ fontWeight: '500', color: '#1a202c' }}>{inquiry.name}</TableCell>
                      <TableCell sx={{ color: '#667085' }}>{inquiry.email}</TableCell>
                      <TableCell sx={{ color: '#667085' }}>{inquiry.subject}</TableCell>
                      <TableCell>
                        <Chip 
                          label={inquiry.status}
                          color={inquiry.status === 'responded' ? 'success' : 'warning'}
                          variant="filled"
                          size="small"
                          sx={{ fontWeight: '500' }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#667085' }}>{new Date(inquiry.created_at).toLocaleDateString()}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Button 
                          size="small"
                          startIcon={<Reply />}
                          onClick={() => handleRespond(inquiry)}
                          sx={{ color: '#2c5f2d' }}
                        >
                          {inquiry.status === 'responded' ? 'View' : 'Respond'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: '#667085' }}>
                      No inquiries found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Footer */}
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTop: '1px solid #e8ecf1' }}>
            <Typography variant="body2" sx={{ color: '#667085' }}>
              Showing {inquiries.length > 0 ? (pageNumber - 1) * pagination.itemsPerPage + 1 : 0} to {Math.min(pageNumber * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} inquiries
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                size="small" 
                disabled={pageNumber === 1}
                onClick={() => setPageNumber(pageNumber - 1)}
              >
                Previous
              </Button>
              {[...Array(pagination.totalPages)].map((_, i) => (
                <Button
                  key={i + 1}
                  variant={pageNumber === i + 1 ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setPageNumber(i + 1)}
                  sx={{
                    minWidth: '36px',
                    backgroundColor: pageNumber === i + 1 ? '#059669' : 'transparent',
                    color: pageNumber === i + 1 ? '#fff' : '#667085',
                    borderColor: '#d5d9e2',
                    '&:hover': {
                      backgroundColor: pageNumber === i + 1 ? '#059669' : '#f3f4f6',
                      borderColor: '#667085'
                    }
                  }}
                >
                  {i + 1}
                </Button>
              ))}
              <Button 
                variant="outlined" 
                size="small"
                disabled={pageNumber === pagination.totalPages || pagination.totalPages === 0}
                onClick={() => setPageNumber(pageNumber + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Card>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: '600', color: '#1a202c' }}>
            {selectedInquiry?.status === 'responded' ? 'View Response' : 'Respond to Inquiry'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedInquiry && (
              <>
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8fafb', borderRadius: '8px', border: '1px solid #e8ecf1' }}>
                  <Typography variant="body2" sx={{ color: '#667085', mb: 1 }}>
                    <strong style={{ color: '#1a202c' }}>From:</strong> {selectedInquiry.name} ({selectedInquiry.email})
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#667085' }}>
                    <strong style={{ color: '#1a202c' }}>Subject:</strong> {selectedInquiry.subject}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#667085', mb: 2, fontWeight: '500' }}>
                  Message:
                </Typography>
                <Box sx={{ p: 2, backgroundColor: '#f8fafb', borderRadius: '8px', border: '1px solid #e8ecf1', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#1a202c', whiteSpace: 'pre-wrap' }}>
                    {selectedInquiry.message}
                  </Typography>
                </Box>

                {/* Show response if already responded */}
                {selectedInquiry.status === 'responded' && selectedInquiry.response && (
                  <>
                    <Typography variant="body2" sx={{ color: '#667085', mb: 2, fontWeight: '500' }}>
                      Your Response:
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #86efac', mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#1a202c', whiteSpace: 'pre-wrap' }}>
                        {selectedInquiry.response}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#667085' }}>
                      Responded on: {new Date(selectedInquiry.responded_at).toLocaleString()}
                    </Typography>
                  </>
                )}

                {/* Show response input only if not yet responded */}
                {selectedInquiry.status !== 'responded' && (
                  <TextField
                    fullWidth
                    label="Your Response"
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    multiline
                    rows={4}
                    placeholder="Type your response here..."
                  />
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenDialog(false)}>
              {selectedInquiry?.status === 'responded' ? 'Close' : 'Cancel'}
            </Button>
            {selectedInquiry?.status !== 'responded' && (
              <Button 
                onClick={handleSendResponse}
                disabled={sending}
                variant="contained"
                sx={{ backgroundColor: '#2c5f2d', '&:hover': { backgroundColor: '#1f4620' } }}
              >
                {sending ? 'Sending...' : 'Send Response'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.type}
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ContactsPage;
